import { Colors, Language, Lightning, Router, Storage } from "@lightningjs/sdk";
import { CategoryList } from "../../components/data-display/CategoryList/CategoryList";
import {MENU_WIDTH, PAGES, STORAGE_KEYS, isPostTvPlatform} from "../../lib/utils";
import { getDashboard } from "../../api/queries";
import { BrandingModel, CategoryModel } from "../../lib/models";
import configs from "../../lib/configs";
import {Branding} from "./components/Branding/Branding";
import { TranslatableText } from "../../components/data-display/TranslatableText/TranslatableText";
import theme from "../../lib/theme";

interface VodTemplateSpec extends Lightning.Component.TemplateSpec {
    Branding: typeof Branding;
    List: typeof CategoryList;
    EmptyMessage: typeof TranslatableText;
}

interface VodTypeConfig extends Lightning.Component.TypeConfig {
    IsPage: true;
}

// Sky youth-protection spec: remind every 6 months (per device) to review
// parental settings. Once per app run, checked when the home page activates.
const PARENTAL_REMINDER_INTERVAL_MS = 182 * 24 * 60 * 60 * 1000;
let parentalReminderEvaluated = false;

export class Vod
    extends Lightning.Component<VodTemplateSpec, VodTypeConfig>
    implements Lightning.Component.ImplementTemplateSpec<VodTemplateSpec>
{
    _lastLocale = Language.get();
    _branding: BrandingModel[] = [];

    static override _template(): Lightning.Component.Template<VodTemplateSpec> {
        return {
            Branding: {
                type: Branding
            },
            List: {
                type: CategoryList,
                x: MENU_WIDTH,
                requestMoreItems: getDashboard
            },
            EmptyMessage: {
                alpha: 0,
                visible: false,
                x: theme.layout.w / 2 + MENU_WIDTH / 2,
                y: theme.layout.h / 2,
                mount: 0.5,
                type: TranslatableText,
                key: "content.empty",
                text: {
                    fontSize: 28,
                    fontFace: "SemiBold",
                    textColor: Colors(theme.colors.text.primary).get(),
                    textAlign: "center",
                    wordWrap: true,
                    wordWrapWidth: 700
                }
            }
        };
    }

    readonly List = this.getByRef("List")!;
    readonly Branding = this.getByRef("Branding")!;
    readonly EmptyMessage = this.getByRef("EmptyMessage")!;

    override _getFocused(): Lightning.Component | null | undefined {
        return this.Branding;
    }

    override _firstActive() {
        this.application.emit("setActivePage", PAGES.vod.name);
    }

    override _active() {
        console.log("configs.branding", configs.hasBranding);
        if (!configs.hasBranding) {
            this._setState("ListState");
        }

        window.seasonSelectedIndex = undefined;
        window.selectedIndex = undefined;
        this.application.emit("setActivePage", PAGES.vod.name);

        this._maybeShowParentalReminder();
    }

    _maybeShowParentalReminder() {
        if (parentalReminderEvaluated) return;
        parentalReminderEvaluated = true;

        if (!(configs.settings?.parentControl === true && isPostTvPlatform())) return;

        const last = Number(Storage.get(STORAGE_KEYS.parentalReminderAt)) || 0;
        if (!last) {
            // First run: set the baseline, first reminder comes 6 months from now.
            Storage.set(STORAGE_KEYS.parentalReminderAt, String(Date.now()));
            return;
        }
        if (Date.now() - last < PARENTAL_REMINDER_INTERVAL_MS) return;

        const reminder = (this as any).widgets?.parentalreminder;
        if (reminder?.open) {
            console.info("[JULIA-ParentalReminder] due, showing dialog");
            reminder.open();
        }
    }

    override _inactive() {
        this._lastLocale = Language.get();

        this.stage.gc();
    }

    override _detach() {
        window.reloadContinueWatching = false;
    }

    // override _handleBack() {
    // if (!Router.getHistory().length) return true;
    // Router.go(Router.getHistory().length * -1);
    // }

    set branding(value: BrandingModel[]) {
        // Null guard — vod loader resolves with undefined branding on content fetch
        // failure to keep app shell usable (see feedback-content-fail-isolation).
        this._branding = value || [];
        this.Branding.patch({
            data: this._branding
        });

        if (!this._branding.length) {
            this._setState("ListState");
        }
    }

    set data(value: CategoryModel[]) {
        this.application.emit("clearBackground");

        // Categories without items are dropped by CategoryList before it reaches
        // the List, so count only those that will actually render
        const safeValue = (value || []).filter((category) => !!category.items?.length);

        if (!safeValue.length) {
            // Empty content state — show user-visible message instead of blank
            // screen. @lightningjs/ui List crashes on empty arrays inside
            // _resizeWrapper, so do NOT call List with [].
            this.EmptyMessage.patch({ alpha: 1, visible: true });
            this._lastLocale = Language.get();
            return;
        }

        this.EmptyMessage.patch({ alpha: 0, visible: false });

        if (this._lastLocale !== Language.get()) {
            this.List.reload(safeValue);
        } else {
            this.List.addItems(safeValue);
        }

        this._lastLocale = Language.get();
    }

    override _handleDown() {
        this._setState("ListState");
    }

    static override _states() {
        return [
            class ListState extends this {
                override _getFocused() {
                    return this.List;
                }

                override _handleUp() {
                    if (this._branding.length) {
                        this._setState("");
                    }
                }

                override $enter() {
                    this.Branding.patch({
                        smooth: {
                            alpha: 0
                        }
                    });
                }

                override $exit() {
                    this.Branding.patch({
                        smooth: {
                            alpha: 1
                        }
                    });
                }
            }
        ];
    }
}
